import React from "react";
import { useSelector } from "react-redux";
import {
  getBusinessById,
  getBusinessRelationsByBusinessId,
  selectVendorCommissionBandIdsByDeploymentBusinessIdLookup,
} from "selectors";

import { ABNValidator } from "lib/validation";
import { PICValidator } from "lib/validation/validators";
import { pluralize } from "lib/pluralize";

import {
  streetNumberValidation,
  routeValidation,
  localityValidation,
  stateValidation,
  postalCodeValidation,
  addressingInformationValidation,
} from "components/Form/Fields/AddressForm/AddressForm";

import * as Yup from "yup";
import {
  accountNumberValidation,
  bsbValidation,
  accountNameValidation,
} from "components/Form/Fields/BankingDetailsForm/BankingDetailsForm";
import { getIn } from "formik";
import { parseSideloadedTransitInsuranceValue } from "lib/insuranceRates";

import {
  LIVESTOCK_AGENT_CONTENT_TYPE_NAME,
  SALEYARD_ADMIN_CONTENT_TYPE_NAME,
} from "constants/users";

export const DEFAULT_SIDE_LOADED_USER_ROLES = [
  LIVESTOCK_AGENT_CONTENT_TYPE_NAME,
  SALEYARD_ADMIN_CONTENT_TYPE_NAME,
];

export const NOTES_FIELD_INDICES = [1, 2, 3, 4, 5] as const;

function SimpleBusinessValueRendererFactory(fieldName: string) {
  const SimpleBusinessValueRenderer = ({ businessId }) => {
    const business = useSelector(getBusinessById(businessId)) || null;
    const value = getIn(business, fieldName) || "-";
    return <span>{value}</span>;
  };
  return SimpleBusinessValueRenderer;
}
export interface FieldConfiguration {
  value: string; // Reference that needs to be unique here.
  targetFieldName: string; // The field name on the business form.
  label: string; // A human readable label for the field.
  validation?: Yup.AnySchema | null; // A Yup Validation function that will be used to validate the field.
  businessValueRenderer?: React.ComponentType<{
    businessId?: string | null;
  }> | null; // A component that will be used to render the current value of the field from the business.
  sourceFieldSuggestions: string[]; // A list of field names from a CSV that will trigger this field as a default value in the source data.
  group?: string; // A string value that indicates the group of the field, used for display purposes.
  handleAsProperty?: boolean; // Properties are handled differently to generic fields.
  handleAsContact?: boolean; // So are contacts.
  index?: number; // A number value that indicates the index when used with contacts or pics.
  fieldName?: string; // A string value that indicates the field name on the business form.   Falls back to value in most cases.
  isBusinessLookup?: boolean; // Whether this field is a business lookup.
  handleAsNotes?: boolean; // Notes allow multiple mappings and are additive to data, rather than replacing it.
  /** Which user roles see this row in the compare modal. */
  forUserRoles: string[];
}

const EmailRecipientsValueRenderer = ({ businessId }) => {
  const business = useSelector(getBusinessById(businessId)) || null;
  if (!business) {
    return null;
  }
  return (
    <span>
      {business.emailRecipients.length} existing{" "}
      {pluralize("contact", business.emailRecipients.length)}.
    </span>
  );
};

const contactConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`contact_email_${index}`] = {
    value: `contact_email_${index}`,
    label: `Contact Email ${index}`,
    businessValueRenderer: index === 1 ? EmailRecipientsValueRenderer : null,
    sourceFieldSuggestions: [`email${index}`].concat(
      index === 1 ? ["email"] : [],
    ),
    index,
    fieldName: "email",
    handleAsContact: true,
    group: index === 1 ? "Contacts" : undefined,
    validation: Yup.string().email("Invalid email"),
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_phone_${index}`] = {
    value: `contact_phone_${index}`,
    label: `Contact Phone ${index}`,
    sourceFieldSuggestions: [`phone${index}`].concat(
      index === 1 ? ["phone"] : [],
    ),
    index,
    fieldName: "phoneNumber",
    handleAsContact: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_first_name_${index}`] = {
    value: `contact_first_name_${index}`,
    label: `Contact First Name ${index}`,
    sourceFieldSuggestions: [`firstname${index}`].concat(
      index === 1 ? ["firstname"] : [],
    ),
    index,
    fieldName: "firstName",
    handleAsContact: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  acc[`contact_last_name_${index}`] = {
    value: `contact_last_name_${index}`,
    label: `Contact Last Name ${index}`,
    sourceFieldSuggestions: [`lastname${index}`].concat(
      index === 1 ? ["lastname"] : [],
    ),
    index,
    fieldName: "lastName",
    handleAsContact: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  return acc;
}, {});

const PropertyValueRenderer = ({ businessId }) => {
  const business = useSelector(getBusinessById(businessId)) || null;
  if (!business) {
    return null;
  }
  return (
    <span>
      {business.properties.length} existing{" "}
      {pluralize("property", business.properties.length)}.
    </span>
  );
};

const propertyConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`PIC_${index}`] = {
    value: `PIC_${index}`,
    label: `PIC ${index}`,
    businessValueRenderer: index === 1 ? PropertyValueRenderer : null,
    sourceFieldSuggestions: [`pic${index}`].concat(
      index === 1 ? ["piccode", "pic"] : [],
    ),
    handleAsProperty: true,
    index,
    group: index === 1 ? "Properties" : undefined,
    validation: PICValidator,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  };
  return acc;
}, {});

const VendorPercentageCommissionValueRenderer = ({ businessId }) => {
  const vendorCommissionBandIdsByDeploymentBusinessIdLookup = useSelector(
    selectVendorCommissionBandIdsByDeploymentBusinessIdLookup,
  );
  const vendorCommissionBandIds =
    vendorCommissionBandIdsByDeploymentBusinessIdLookup[businessId] || [];
  return (
    <span>
      {vendorCommissionBandIds.length} existing{" "}
      {pluralize("vendor commission band", vendorCommissionBandIds.length)}.
    </span>
  );
};

const RelationshipValueRenderer = ({ businessId }) => {
  const businessRelationsByBusinessId = useSelector(
    getBusinessRelationsByBusinessId,
  );
  const businessRelations = businessRelationsByBusinessId[businessId] || [];
  return (
    <span>
      {businessRelations.length} existing{" "}
      {pluralize("business relation", businessRelations.length)}.
    </span>
  );
};

export const fieldConfigurations: Record<string, FieldConfiguration> = {
  // Simple "General Collapse" fields.
  NAME: {
    value: "name",
    targetFieldName: "name",
    label: "Name",
    validation: null,
    businessValueRenderer: SimpleBusinessValueRendererFactory("name"),
    sourceFieldSuggestions: ["name", "displayname", "tradingname"],
    group: "General",
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  PUBLIC_DISPLAY_NAME: {
    value: "publicDisplayName",
    targetFieldName: "publicDisplayName",
    label: "Public Display Name",
    validation: null,
    businessValueRenderer:
      SimpleBusinessValueRendererFactory("publicDisplayName"),
    sourceFieldSuggestions: [],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE: {
    value: "shortCode",
    targetFieldName: "shortCode",
    label: "Agent Reference",
    validation: null,
    businessValueRenderer: SimpleBusinessValueRendererFactory("shortCode"),
    sourceFieldSuggestions: ["agentreference", "code", "agentcode", "agentref"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE_SALEYARD: {
    value: "shortCodeSaleyard",
    targetFieldName: "shortCodeSaleyard",
    label: "Saleyard Reference",
    validation: null,
    businessValueRenderer:
      SimpleBusinessValueRendererFactory("shortCodeSaleyard"),
    sourceFieldSuggestions: [
      "saleyardreference",
      "saleyardcode",
      "syref",
      "sycode",
    ],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  SHORT_CODE_AUCTIONS_PLUS: {
    value: "shortCodeAuctionsPlus",
    targetFieldName: "shortCodeAuctionsPlus",
    label: "Auctions Plus Shortcode",
    validation: null,
    businessValueRenderer: SimpleBusinessValueRendererFactory(
      "shortCodeAuctionsPlus",
    ),
    sourceFieldSuggestions: ["auctionsplusshortcode", "auctionsplus"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  BRANDS: {
    value: "brands",
    targetFieldName: "brands",
    label: "Brands",
    validation: null,
    businessValueRenderer: SimpleBusinessValueRendererFactory("brands"),
    sourceFieldSuggestions: ["brand", "brands"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  // TODO booleans..
  // Address fields.
  ADDRESS_ADRESSING_INFORMATION: {
    value: "addressAddressingInformation",
    label: "Address Addressing Information",
    targetFieldName: "address.addressingInformation",
    validation: addressingInformationValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory(
      "address.addressingInformation",
    ),
    sourceFieldSuggestions: ["address1"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_STREET_NUMBER: {
    value: "addressStreetNumber",
    label: "Address Street Number",
    targetFieldName: "address.streetNumber",
    validation: streetNumberValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory(
      "address.streetNumber",
    ),
    sourceFieldSuggestions: ["streetnumber", "addressstreetnumber"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_ROUTE: {
    value: "addressRoute",
    label: "Address Street Name",
    targetFieldName: "address.route",
    validation: routeValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory("address.route"),
    sourceFieldSuggestions: ["addressroute", "streetname", "street"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_LOCALITY: {
    value: "addressLocality",
    label: "Address City",
    targetFieldName: "address.locality",
    validation: localityValidation,
    businessValueRenderer:
      SimpleBusinessValueRendererFactory("address.locality"),
    sourceFieldSuggestions: ["locality", "town", "city", "suburb", "address2"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_STATE: {
    value: "addressState",
    label: "Address State",
    targetFieldName: "address.state",
    validation: stateValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory("address.state"),
    sourceFieldSuggestions: ["addressstate", "state", "statename"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ADDRESS_POSTAL_CODE: {
    value: "addressPostalCode",
    label: "Address Post Code",
    targetFieldName: "address.postalCode",
    validation: postalCodeValidation,
    businessValueRenderer:
      SimpleBusinessValueRendererFactory("address.postalCode"),
    sourceFieldSuggestions: ["postcode", "postalcode", "physicalpostcode"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  // Financial fields.
  BSB: {
    value: "bankingDetailsBsb",
    label: "BSB",
    targetFieldName: "bankingDetails.bsb",
    validation: bsbValidation,
    businessValueRenderer:
      SimpleBusinessValueRendererFactory("bankingDetails.bsb"),
    sourceFieldSuggestions: ["bsb", "bsbnumber"],
    group: "Financial",
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
  ACCOUNT_NUMBER: {
    value: "bankingDetailsAccountNumber",
    label: "Account Number",
    targetFieldName: "bankingDetails.accountNumber",
    validation: accountNumberValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory(
      "bankingDetails.accountNumber",
    ),
    sourceFieldSuggestions: ["accountnumber", "account"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  ACCOUNT_NAME: {
    value: "bankingDetailsAccountName",
    label: "Account Name",
    targetFieldName: "bankingDetails.accountName",
    validation: accountNameValidation,
    businessValueRenderer: SimpleBusinessValueRendererFactory(
      "bankingDetails.accountName",
    ),
    sourceFieldSuggestions: ["accountname", "accname"],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  ABN: {
    value: "abn",
    label: "ABN",
    targetFieldName: "abn",
    businessValueRenderer: SimpleBusinessValueRendererFactory("abn"),
    sourceFieldSuggestions: ["abn"],
    validation: ABNValidator,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  TRANSIT_INSURANCE: {
    value: "transitInsurance",
    targetFieldName: "__transitInsurance",
    label: "Transit Insurance",
    validation: Yup.string().test({
      name: "validTransitInsuranceValue",
      message: "Transit Insurance must be Buy, Sell, Buy/Sell, or empty.",
      test: value =>
        value == null ||
        value === "" ||
        parseSideloadedTransitInsuranceValue(value) !== undefined,
    }),
    sourceFieldSuggestions: [
      "transitinsurance",
      "transitinsuranceapplication",
      "transitinsurancebuysell",
    ],
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },

  // Enumerated fields - PICs
  ...propertyConfigurations,

  // Enumerated - Contacts
  ...contactConfigurations,

  // Agency Commissions
  VENDOR_PERCENTAGE_COMMISSION: {
    value: "vendorPercentageCommission",
    label: "Agency Commission Override",
    targetFieldName: "vendorPercentageCommission",
    sourceFieldSuggestions: ["comm", "comms"],
    validation: Yup.number().min(0).max(100),
    group: "Commissions",
    businessValueRenderer: VendorPercentageCommissionValueRenderer,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  // - Stock Agent
  STOCK_AGENT: {
    value: "stockAgent",
    label: "Stock Agent",
    targetFieldName: "stockAgent",
    sourceFieldSuggestions: ["agent", "stockagent"],
    validation: null, // This is handled upstream in the form declaration.
    isBusinessLookup: true,
    businessValueRenderer: RelationshipValueRenderer,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  STOCK_AGENT_PERCENTAGE_COMMISSION: {
    value: "stockAgentPercentageCommission",
    label: "Stock Agent Percentage Commission",
    targetFieldName: "stockAgentPercentageCommission",
    sourceFieldSuggestions: ["agentcommissions"],
    validation: null, // This is handled upstream in the form declaration.
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  // Currently a single entry - consider if we need multiple?
  REBATE_AGENT: {
    value: "rebateAgent",
    label: "Rebate Agent",
    targetFieldName: "rebateAgent",
    sourceFieldSuggestions: ["rebagent", "rebateagent"],
    validation: null, // This is handled upstream in the form declaration.
    isBusinessLookup: true,
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },
  REBATE_AGENT_PERCENTAGE_COMMISSION: {
    value: "rebateAgentPercentageCommission",
    label: "Rebate Agent Percentage Commission",
    targetFieldName: "rebateAgentPercentageCommission",
    sourceFieldSuggestions: [
      "impliedcommissions",
      "calculatedrebatecommission",
    ],
    validation: null, // This is handled upstream in the form declaration.
    forUserRoles: [LIVESTOCK_AGENT_CONTENT_TYPE_NAME],
  },

  NOTES: {
    value: "notes",
    targetFieldName: "notes",
    label: "Notes",
    validation: null,
    businessValueRenderer: SimpleBusinessValueRendererFactory("notes"),
    sourceFieldSuggestions: [
      "note",
      "notes",
      "extra",
      "more",
      "commissionnotes",
    ],
    group: "Notes",
    handleAsNotes: true,
    forUserRoles: DEFAULT_SIDE_LOADED_USER_ROLES,
  },
};

/**
 * Field rows shown in the sideload compare modal for the current user.
 * Saleyard admins use SaleyardBusiness payloads without relationships / vendor commission bands.
 */
export function getSideLoadedFieldConfigurationsForUser(
  userRole: string | null,
): FieldConfiguration[] {
  if (userRole === null) {
    return [];
  }
  return Object.values(fieldConfigurations).filter(configuration =>
    configuration.forUserRoles.includes(userRole),
  );
}
